import { useRef, useEffect } from 'react'
import MessageBubble from './MessageBubble'
import VisitorInfoPanel from './VisitorInfoPanel'
import { CheckIcon, FlagIcon, DocumentDuplicateIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export default function ConversationDetail({ conversation, messages, onStatusChange, loading }) {
  const messagesEndRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50 rounded-xl">
        <div className="text-center">
          <p className="text-sm text-slate-400">Select a conversation to view details</p>
        </div>
      </div>
    )
  }

  const copyTranscript = () => {
    const text = messages
      .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
      .join('\n\n')
    navigator.clipboard.writeText(text)
    toast.success('Transcript copied')
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Conversation
            </h3>
            <p className="text-[10px] text-slate-400">
              {conversation.agent_used || 'General'} agent · {conversation.status}
            </p>
          </div>
          {loading && <ArrowPathIcon className="h-4 w-4 text-slate-400 animate-spin" />}
        </div>
        <VisitorInfoPanel
          visitorMetadata={conversation.visitor_metadata}
          conversation={conversation}
        />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center space-x-2 flex-shrink-0">
        {conversation.status !== 'resolved' && (
          <button
            onClick={() => onStatusChange('resolved')}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <CheckIcon className="h-3.5 w-3.5" />
            <span>Resolve</span>
          </button>
        )}
        {conversation.status !== 'flagged' && (
          <button
            onClick={() => onStatusChange('flagged')}
            className="flex items-center space-x-1.5 px-3 py-1.5 border border-amber-200 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-50 transition-colors"
          >
            <FlagIcon className="h-3.5 w-3.5" />
            <span>Flag</span>
          </button>
        )}
        <button
          onClick={copyTranscript}
          className="flex items-center space-x-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors"
        >
          <DocumentDuplicateIcon className="h-3.5 w-3.5" />
          <span>Copy</span>
        </button>
      </div>
    </div>
  )
}
